/**
 * Self-Hosted Auth Store (Zustand)
 *
 * Manages device authentication and pairing for self-hosted Happy relay server.
 *
 * IMPORTANT: Auth tokens are now stored in HTTP-only cookies (not localStorage).
 * This store only persists device metadata (name, URL) - NOT tokens.
 * The server sets/reads tokens via secure cookies for XSS protection.
 */

import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { useMemo } from 'react';
import { hasAuthCookies } from '../services/happyAuth';
import { getMyDevices, type MyDevice } from '../services/vibeRemoteAuth';

/**
 * Self-hosted device representation
 */
export interface SelfHostedDevice {
  id: string;
  name: string;
  relayUrl: string;
  lastConnected?: Date;
  isPaired: boolean;
  createdAt: Date;
}

/**
 * Pairing state for device authentication
 */
export interface PairingState {
  code: string | null;
  expiry: Date | null;
  isRequesting: boolean;
  error: string | null;
}

/**
 * Self-Hosted Auth Store State
 *
 * NOTE: Tokens are NO LONGER stored here - they're in HTTP-only cookies.
 * This store only tracks UI state and device metadata.
 */
interface SelfHostedAuthState {
  // Device management
  devices: SelfHostedDevice[];
  activeDevice: SelfHostedDevice | null;

  // Pairing flow
  pairingCode: string | null;
  pairingExpiry: Date | null;

  // Connection state
  isConnecting: boolean;
  connectionError: string | null;

  // Demo mode - allows using dashboard without device pairing
  // This state persists across route navigation (/vibe-remote -> /vibe-remote/cli)
  demoMode: boolean;

  // DEPRECATED: Token storage moved to HTTP-only cookies
  // Kept for migration/backwards compatibility only
  deviceTokens: Record<string, { accessToken: string; refreshToken: string; expiresAt: number }>;
}

export type DeviceToken = {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
};

export type { SelfHostedAuthState };

/**
 * Self-Hosted Auth Store Actions
 */
interface SelfHostedAuthActions {
  // Device management
  addDevice: (name: string, relayUrl: string) => string;
  removeDevice: (deviceId: string) => void;
  updateDevice: (deviceId: string, updates: Partial<SelfHostedDevice>) => void;
  setActiveDevice: (deviceId: string | null) => void;
  markDeviceConnected: (deviceId: string) => void;
  markDevicePaired: (deviceId: string, isPaired: boolean) => void;

  // Pairing flow
  startPairing: (code: string, expiresInSeconds: number) => void;
  cancelPairing: () => void;
  completePairing: (deviceId: string, accessToken: string, refreshToken: string, expiresIn: number) => void;

  // Connection state
  setConnecting: (isConnecting: boolean) => void;
  setConnectionError: (error: string | null) => void;
  clearError: () => void;

  // Token management (DEPRECATED - tokens now in HTTP-only cookies)
  // These methods are kept for backwards compatibility during migration
  getDeviceToken: (deviceId: string) => { accessToken: string; refreshToken: string } | null;
  setDeviceToken: (deviceId: string, accessToken: string, refreshToken: string, expiresIn: number) => void;
  clearDeviceToken: (deviceId: string) => void;
  isTokenExpired: (deviceId: string) => boolean;

  // Cookie-based auth helpers
  isAuthenticated: () => boolean;

  // Demo mode
  setDemoMode: (enabled: boolean) => void;
  isDemoMode: () => boolean;

  // Sync devices from relay server (for CLI-based pairing)
  syncDevicesFromRelay: (serverUrl?: string) => Promise<boolean>;

  // Reset
  reset: () => void;
}

/**
 * Initial state
 */
const initialState: SelfHostedAuthState = {
  devices: [],
  activeDevice: null,
  pairingCode: null,
  pairingExpiry: null,
  isConnecting: false,
  connectionError: null,
  demoMode: false,
  deviceTokens: {},
};

/**
 * Generate unique device ID
 */
function generateDeviceId(): string {
  return `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Self-Hosted Auth Zustand Store
 *
 * Manages device authentication for self-hosted Happy relay servers.
 * Features:
 * - Multiple device support
 * - Device pairing with 6-digit codes
 * - Token storage per device
 * - Connection state management
 *
 * @example
 * ```tsx
 * const { devices, activeDevice, addDevice } = useSelfHostedAuthStore();
 * ```
 */
export const useSelfHostedAuthStore = create<SelfHostedAuthState & SelfHostedAuthActions>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // Device management
        addDevice: (name: string, relayUrl: string) => {
          const id = generateDeviceId();
          const newDevice: SelfHostedDevice = {
            id,
            name,
            relayUrl: relayUrl.replace(/\/$/, ''), // Remove trailing slash
            isPaired: false,
            createdAt: new Date(),
          };

          set((state) => ({
            devices: [...state.devices, newDevice],
          }));

          console.log(`[SelfHostedAuth] Added device: ${name} (${id})`);
          return id;
        },

        removeDevice: (deviceId: string) => {
          set((state) => {
            const newDevices = state.devices.filter((d) => d.id !== deviceId);
            const newTokens = { ...state.deviceTokens };
            delete newTokens[deviceId];

            return {
              devices: newDevices,
              activeDevice: state.activeDevice?.id === deviceId ? null : state.activeDevice,
              deviceTokens: newTokens,
            };
          });
          console.log(`[SelfHostedAuth] Removed device: ${deviceId}`);
        },

        updateDevice: (deviceId: string, updates: Partial<SelfHostedDevice>) => {
          set((state) => ({
            devices: state.devices.map((d) =>
              d.id === deviceId ? { ...d, ...updates } : d
            ),
            activeDevice:
              state.activeDevice?.id === deviceId
                ? { ...state.activeDevice, ...updates }
                : state.activeDevice,
          }));
        },

        setActiveDevice: (deviceId: string | null) => {
          const device = deviceId
            ? get().devices.find((d) => d.id === deviceId) || null
            : null;

          set({
            activeDevice: device,
            connectionError: null,
          });

          console.log(`[SelfHostedAuth] Active device set to: ${device?.name || 'none'}`);
        },

        markDeviceConnected: (deviceId: string) => {
          get().updateDevice(deviceId, { lastConnected: new Date() });
        },

        markDevicePaired: (deviceId: string, isPaired: boolean) => {
          get().updateDevice(deviceId, { isPaired });
        },

        // Pairing flow
        startPairing: (code: string, expiresInSeconds: number) => {
          const expiryDate = new Date(Date.now() + expiresInSeconds * 1000);
          set({
            pairingCode: code,
            pairingExpiry: expiryDate,
            connectionError: null,
          });
          console.log(`[SelfHostedAuth] Pairing started with code: ${code}`);
        },

        cancelPairing: () => {
          set({
            pairingCode: null,
            pairingExpiry: null,
          });
          console.log(`[SelfHostedAuth] Pairing cancelled`);
        },

        completePairing: (deviceId: string, accessToken: string, refreshToken: string, expiresIn: number) => {
          get().markDevicePaired(deviceId, true);
          get().setDeviceToken(deviceId, accessToken, refreshToken, expiresIn);
          set({
            pairingCode: null,
            pairingExpiry: null,
          });
          console.log(`[SelfHostedAuth] Pairing completed for device: ${deviceId}`);
        },

        // Connection state
        setConnecting: (isConnecting: boolean) => {
          set({ isConnecting });
        },

        setConnectionError: (error: string | null) => {
          set({ connectionError: error, isConnecting: false });
        },

        clearError: () => {
          set({ connectionError: null });
        },

        // Token management
        getDeviceToken: (deviceId: string) => {
          const tokenData = get().deviceTokens[deviceId];
          if (!tokenData) return null;
          return {
            accessToken: tokenData.accessToken,
            refreshToken: tokenData.refreshToken,
          };
        },

        setDeviceToken: (deviceId: string, accessToken: string, refreshToken: string, expiresIn: number) => {
          set((state) => ({
            deviceTokens: {
              ...state.deviceTokens,
              [deviceId]: {
                accessToken,
                refreshToken,
                expiresAt: Date.now() + expiresIn * 1000,
              },
            },
          }));
        },

        clearDeviceToken: (deviceId: string) => {
          set((state) => {
            const newTokens = { ...state.deviceTokens };
            delete newTokens[deviceId];
            return { deviceTokens: newTokens };
          });
        },

        isTokenExpired: (deviceId: string) => {
          // DEPRECATED: Token expiry is now handled by HTTP-only cookies
          // This check is only for migration/backwards compatibility
          const tokenData = get().deviceTokens[deviceId];
          if (!tokenData) {
            // No local token - check if we have a cookie-based session
            // If device ID cookie exists, we're authenticated
            return !hasAuthCookies();
          }
          return Date.now() >= tokenData.expiresAt;
        },

        // Cookie-based auth helpers
        isAuthenticated: () => {
          // Check if device is authenticated via HTTP-only cookies
          return hasAuthCookies();
        },

        // Demo mode
        setDemoMode: (enabled: boolean) => {
          set({ demoMode: enabled });
          console.log(`[SelfHostedAuth] Demo mode ${enabled ? 'enabled' : 'disabled'}`);
        },

        isDemoMode: () => {
          return get().demoMode;
        },

        // Sync devices from relay server (for CLI-based pairing)
        syncDevicesFromRelay: async (serverUrl?: string): Promise<boolean> => {
          try {
            console.log('[SelfHostedAuth] Syncing devices from relay...');
            const response = await getMyDevices(serverUrl);

            if (!response.authenticated || response.devices.length === 0) {
              console.log('[SelfHostedAuth] No authenticated devices from relay');
              return false;
            }

            // Register each device from the relay
            const state = get();
            let newDevicesAdded = false;

            for (const device of response.devices) {
              // Check if device already exists
              const existingDevice = state.devices.find(d => d.id === device.id);

              if (existingDevice) {
                // Update existing device
                console.log(`[SelfHostedAuth] Updating existing device: ${device.id}`);
                get().updateDevice(device.id, {
                  isPaired: device.isPaired,
                  lastConnected: new Date(),
                });
              } else {
                // Add new device from relay
                console.log(`[SelfHostedAuth] Adding new device from relay: ${device.id} (${device.name})`);
                const newDevice: SelfHostedDevice = {
                  id: device.id,
                  name: device.name,
                  relayUrl: device.relayUrl,
                  isPaired: device.isPaired,
                  createdAt: new Date(device.pairedAt),
                  lastConnected: new Date(),
                };

                set((s) => ({
                  devices: [...s.devices, newDevice],
                  activeDevice: s.activeDevice || newDevice, // Set as active if no active device
                }));

                newDevicesAdded = true;
              }
            }

            if (newDevicesAdded) {
              console.log(`[SelfHostedAuth] Synced ${response.devices.length} device(s) from relay`);
            }

            return true;
          } catch (error) {
            console.error('[SelfHostedAuth] Error syncing devices from relay:', error);
            return false;
          }
        },

        // Reset
        reset: () => set(initialState),
      }),
      {
        name: 'self-hosted-auth-storage',
        // IMPORTANT: Only persist device metadata, NOT tokens
        // Tokens are now in HTTP-only cookies for security
        partialize: (state) => ({
          devices: state.devices,
          demoMode: state.demoMode,
          // deviceTokens intentionally NOT persisted anymore
        }),
        // Custom deserializer to handle Date objects
        onRehydrateStorage: () => (state) => {
          if (state) {
            // Convert date strings back to Date objects
            state.devices = state.devices.map((d) => ({
              ...d,
              createdAt: new Date(d.createdAt),
              lastConnected: d.lastConnected ? new Date(d.lastConnected) : undefined,
            }));
          }
        },
      }
    ),
    { name: 'self-hosted-auth-store' }
  )
);

/**
 * Selector hooks for specific state slices
 *
 * IMPORTANT: Selectors that return arrays/objects must use useShallow or useMemo
 * to prevent infinite loops from creating new references on every render.
 */

// Stable empty array for when there are no paired devices
const EMPTY_DEVICES: SelfHostedDevice[] = [];

/** Get all paired devices - uses useMemo for stable filtered result */
export function usePairedDevices(): SelfHostedDevice[] {
  const devices = useSelfHostedAuthStore((state) => state.devices);
  // useMemo ensures we only recalculate when devices changes
  return useMemo(() => {
    const paired = devices.filter((d) => d.isPaired);
    return paired.length > 0 ? paired : EMPTY_DEVICES;
  }, [devices]);
}

/** Get active device - returns primitive or null (stable reference) */
export function useActiveDevice(): SelfHostedDevice | null {
  return useSelfHostedAuthStore((state) => state.activeDevice);
}

/** Check if currently pairing - returns primitive boolean */
export function useIsPairing(): boolean {
  return useSelfHostedAuthStore((state) => state.pairingCode !== null);
}

/** Get pairing code - returns primitive string or null */
export function usePairingCode(): string | null {
  return useSelfHostedAuthStore((state) => state.pairingCode);
}

/** Get pairing expiry - returns primitive Date or null */
export function usePairingExpiry(): Date | null {
  return useSelfHostedAuthStore((state) => state.pairingExpiry);
}

/** Check if connecting - returns primitive boolean */
export function useIsConnecting(): boolean {
  return useSelfHostedAuthStore((state) => state.isConnecting);
}

/** Get connection error - returns primitive string or null */
export function useConnectionError(): string | null {
  return useSelfHostedAuthStore((state) => state.connectionError);
}

/** Check if authenticated via HTTP-only cookies */
export function useIsAuthenticated(): boolean {
  return useSelfHostedAuthStore((state) => state.isAuthenticated());
}

/** Check if demo mode is enabled - persists across route navigation */
export function useDemoMode(): boolean {
  return useSelfHostedAuthStore((state) => state.demoMode);
}
